import Vue from 'vue'
const dayjs = require('dayjs')
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)
import utc from  'dayjs/plugin/utc'
import FileUploader from "@/components/blog/FileUploader";
import * as Vibrant from 'node-vibrant'
dayjs.extend(utc)

// Sentinel for an empty saved-color slot. Matched by exact string against persisted
// saved_colors, so the value must stay stable — the 0.95 alpha is part of the identity
// key, not a meaningful opacity. Shared so the copies in Preview/DraftColors can't drift.
export const NO_COLOR = 'hsla(0, 0%, 50%, 0.95)'

const util = new (Vue.extend({
	methods: {
		sortBy(items, key, dir = 'asc') {
			return [...items].sort((a, b) => {
				let av = a[key] ?? '', bv = b[key] ?? ''
				const cmp = typeof av === 'string' ? av.localeCompare(bv, undefined, { sensitivity: 'base' }) : av - bv
				return dir === 'asc' ? cmp : -cmp
			})
		},
		formatDate(date) {
			if (date) return dayjs(date).format('MMMM D, YYYY')
			return null
		},

		toUtc(date) {
			return dayjs(date).utc()
		},

		formatDateTo(date, format) {
			if (!format) format = 'YYYY-MM-DD'
			if (date && format) return dayjs(date).utc().format(format)
			return null
		},

		formatPacificDate(date, showSpecifics = true) {
			if (date) {
				const isPST = dayjs(date).$d.toString().includes('Pacific Standard Time')
				let tz = isPST ? 'PST' : 'PDT'
				let gmtDiff = isPST ? 8 : 7
				return showSpecifics ? `${dayjs(date).subtract(gmtDiff, 'hour').format('MMM D, YYYY HH:mm')} ${tz}` : `${dayjs(date).subtract(gmtDiff, 'hour').format('MMMM D, YYYY')}`
			}
			return null
		},

		timeAgo(time, valueOnly) {
			if (time) return dayjs(time).fromNow(valueOnly)
			return null
		},

		dateDiff(date1, date2, unit) {
			const date = dayjs(date1)
			return date.diff(date2, unit)
		},

		getStartOfWeek(year, week){
			const date = new Date(year, 0, 1)
			if (week === 1) return date
			else {
				date.setDate(date.getDate() + 7 - date.getDay() + (week - 2)*7)
				return date.toDateString()
			}
		},
		isEmail(email) {
			// eslint-disable-next-line
			return /^([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22))*\x40([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d))*$/.test(email)
		},

		ensurePeriodEnd(text) {
			text = text.trim()
			if (!text.endsWith('.') && !text.endsWith('?') && !text.endsWith('!')) text = text + '.'
			return text
		},

		isProduction() {
			return process.env.NODE_ENV === 'production'
		},

		formatTime(dateTime) {
			if (dateTime) return dayjs(dateTime).format('h:mm A')
			return null
		},

		formatNumber(number) { // format with thousands separators
			return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		},

		async uploadImage(e) {
			const files = e.target.files
			let imageId = null
			let imageBlogUri = null
			let imagePhotosUri = null
			let file = files[0]
			let image = await FileUploader.submitFile(file)
			let blockIds = []
			let currentFilePointer = 0
			let totalBytesRemaining = file.size
			let nextBlock = await FileUploader.getAndUploadNextBlock(file, blockIds, currentFilePointer, totalBytesRemaining, image.image_upload_uri)
			while (nextBlock !== null) {
				nextBlock = await FileUploader.getAndUploadNextBlock(file, blockIds, currentFilePointer, totalBytesRemaining, image.image_upload_uri)
			}
			let blockListCommited = await FileUploader.commitBlockList(file, blockIds, image.image_upload_uri)
			if (blockListCommited){
				let patched = await FileUploader.patchFile(image)
				if (patched) {
					imageId = image.blog_image_id
					imageBlogUri = image.image_download_uri
					if (util.isProduction()) {
						// This is a special case where when we upload images we can also access it through the images
						// url of cloudflare which is useful for images or videos that are used on the extension
						imagePhotosUri = 'https://momentum.photos/assets/' + imageBlogUri.split('/').at(-1)
					} else {
						imagePhotosUri = image.image_download_uri
					}
				} else {
					imageId = null
				}
			} else {
				imageId = null
			}
			return [imageId, imageBlogUri, imagePhotosUri]
		},

		async getDistinctId(userUuid) {
			if (!userUuid) return null
			// UUID hex -> int(bytes) in Uint8Array -> Uint8Array from digest -> Distinct ID in hex
			const byteArray = util.uuidToByteArray(userUuid)
			const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(byteArray))
			return new Uint8Array(digest).reduce((hexDigest, byte) => {
				return hexDigest + byte.toString(16).padStart(2, '0')
			}, '')
		},

		uuidToByteArray: function (uuid) {
			let stripped = uuid?.replaceAll('-', '')
			if (!stripped) return null
			const byteArray = []
			while (stripped.length > 0) {
				let byte = stripped.slice(0, 2)
				stripped = stripped.slice(2)
				byteArray.push(parseInt(byte, 16))
			}
			return byteArray
		},

		// Parse an `hsl(...)`/`hsla(...)` string into { h, s, l } numbers, tolerating
		// commas or spaces, `%` signs, a missing alpha, and even a missing closing
		// paren (the eyedropper used to emit `hsla(..., 0.95`). Returns null when the
		// hue/saturation/lightness can't be read.
		parseHsla(hsla) {
			if (typeof hsla !== 'string' || hsla.indexOf('(') === -1) return null
			const inside = hsla.slice(hsla.indexOf('(') + 1).replace(')', '')
			const parts = (inside.includes(',') ? inside.split(',') : inside.split(/\s+/))
				.map(part => part.trim().replace('%', ''))
				.filter(part => part.length)
			if (parts.length < 3) return null
			const [h, s, l] = parts.map(Number)
			if (![h, s, l].every(Number.isFinite)) return null
			return { h, s, l }
		},

		hueFromHsla(hsla) {
			const parsed = util.parseHsla(hsla)
			return parsed ? Math.round(parsed.h) : 0
		},

		// Rebuild a color string with a new hue, keeping the existing saturation and
		// lightness. Falls back to a neutral grey when the source can't be parsed.
		withHue(hsla, hue) {
			const parsed = util.parseHsla(hsla) || { s: 0, l: 50 }
			const h = ((Math.round(Number(hue)) % 360) + 360) % 360
			return `hsl(${h}, ${Math.round(parsed.s)}%, ${Math.round(parsed.l)}%)`
		},

		calculateLuminosity(r, g, b) {
			// https://www.w3.org/WAI/GL/wiki/Relative_luminance
			const linear = x => {
				const xs = x / 255
				return xs <= 0.03928 ? xs / 12.92 : Math.pow((xs + 0.055) / 1.055, 2.4)
			}
			return linear(r) * 0.2126 + linear(g) * 0.7152 + linear(b) * 0.0722
		},

		calculateContrastRatio(luminosity, textColor) {
			const luminosityOfWhite = 1
			const luminosityOfOurBlack = util.calculateLuminosity(34, 34, 34)
			const ratio = textColor === '#fff'
				? (luminosityOfWhite + 0.05) / (luminosity + 0.05)
				: (luminosity + 0.05) / (luminosityOfOurBlack + 0.05)
			return ratio.toFixed(2)
		},

		// Single source of truth for "given an hsla, what's the full color object".
		// Returns every field the UI and saved widget_color rely on, so callers never
		// depend on ContrastRatio's reactivity to backfill rgba/hex/etc. Colors are
		// emitted opaque: opacity is a render-time concern owned by each consumer (the
		// editor preview, and ultimately the extension), not baked into stored data.
		// The string fields keep their historical `hsla`/`rgba` keys for compatibility.
		deriveColorFromHsla(hsla) {
			const { h, s, l } = util.parseHsla(hsla) || { h: 0, s: 0, l: 50 }
			const rgb = Vibrant.Util.hslToRgb(h / 360, s / 100, l / 100)
			const luminosity = util.calculateLuminosity(rgb[0], rgb[1], rgb[2])
			const bodyTextColor = luminosity > 0.3 ? '#222' : '#fff'
			return {
				hsl: [Math.round(h), Math.round(s), Math.round(l)],
				hsla: `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`,
				rgba: `rgb(${rgb[0].toFixed()}, ${rgb[1].toFixed()}, ${rgb[2].toFixed()})`,
				hex: Vibrant.Util.rgbToHex(Math.round(rgb[0]), Math.round(rgb[1]), Math.round(rgb[2])),
				bodyTextColor,
				contrastRatio: util.calculateContrastRatio(luminosity, bodyTextColor),
				hue: Math.round(h),
			}
		},

		// The pristine 9-slot saved-colors palette shown before a photo has any saved
		// colors. Shared so the dirty-tracking baseline (Preview) and the renderer
		// (DraftColors) agree on the exact shape — including key order — no matter when
		// saved_colors materializes from undefined into the array.
		defaultSavedColors() {
			return Array.from({ length: 9 }, (_, index) => ({ hsla: NO_COLOR, name: `momo-${index}` }))
		},
	},
}))()

export default util
